import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import { 
  XCircle, 
  ArrowRight, 
  Home, 
  RefreshCw,
  MessageSquare,
  AlertTriangle
} from 'lucide-react';

export default function PaymentCancelledPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [transferInfo, setTransferInfo] = useState(null);

  useEffect(() => {
    // Try to get transfer info from localStorage
    const pendingTransfers = JSON.parse(localStorage.getItem('pending_transfers') || '[]');
    if (pendingTransfers.length > 0) {
      const latestTransfer = pendingTransfers[pendingTransfers.length - 1];
      setTransferInfo(latestTransfer);
    }
  }, []);

  const handleRetry = () => {
    // Navigate back to send money with pre-filled data if available
    if (transferInfo) {
      navigate('/send-money', { 
        state: { 
          amount: transferInfo.fiat_amount,
          currency: transferInfo.fiat_currency,
          recipientName: transferInfo.recipient_name,
          recipientCountry: transferInfo.recipient_country
        }
      });
    } else {
      navigate('/send-money');
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-gray-50 via-slate-50 to-zinc-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-lg mx-auto">
          {/* Cancelled Card */}
          <Card className="shadow-xl border-0 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-400 to-orange-400 h-2" />
            <CardHeader className="text-center pt-8">
              <div className="mx-auto w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-12 h-12 text-amber-600" />
              </div>
              <CardTitle className="text-2xl text-gray-800">
                Payment Cancelled
              </CardTitle>
              <CardDescription className="text-base mt-2">
                Your transfer was not completed. No money has been deducted from your account.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pb-8">
              {/* Reason Info */}
              <div className="bg-amber-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">What happened?</p>
                    <p className="text-sm text-amber-700 mt-1">
                      The payment was cancelled or timed out. This could be due to:
                    </p>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1 list-disc list-inside">
                      <li>You cancelled the payment</li>
                      <li>The payment session expired</li>
                      <li>There was an issue with your payment method</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Transfer Details (if available) */}
              {transferInfo && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-gray-700">Transfer Attempted</h4>
                  {transferInfo.fiat_amount && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount</span>
                      <span className="font-medium">{transferInfo.fiat_amount} {transferInfo.fiat_currency || 'USD'}</span>
                    </div>
                  )}
                  {transferInfo.recipient_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Recipient</span>
                      <span className="font-medium">{transferInfo.recipient_name}</span>
                    </div>
                  )}
                  {transferInfo.recipient_country && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Destination</span>
                      <span className="font-medium">{transferInfo.recipient_country}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3 pt-4">
                <Button 
                  onClick={handleRetry}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Try Again
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => navigate('/contact')}
                  className="w-full"
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> Contact Support
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  <Home className="w-4 h-4 mr-2" /> Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Help Info */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Having trouble? We're here to help.</p>
            <p className="mt-1">
              Email us at <a href="mailto:support@routeflowai.com" className="text-emerald-600 hover:underline">support@routeflowai.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
